// Messaging workspace (FE-30, route /messages): conversations, the open thread with delivery states,
// consent and opt-out display, and the composer. Texting stays gated by the `messaging.sms`
// capability: until the carriers approve the 10DLC campaign every send control is disabled with the
// server's own reason.
import type { EventEnvelope } from '@bcio-voice/contracts';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { BellRing, Bot, ChevronLeft, Hourglass, Info, MessageSquarePlus, ShieldX } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { ErrorPanel, SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { Link, useRouter } from '../../lib/router.tsx';
import { hasPermission } from '../../lib/capabilities.ts';
import { formatAbsolute, formatPhone, formatRelative } from '../../lib/format.ts';
import { type ConsentState, type Message, type MessageThread, MessagingClient } from '../../lib/api.ts';
import type { StreamStatus } from '../../lib/events.ts';
import { MessagingController, type MessagingSnapshot, type ThreadView } from './controller.ts';
import { messagingEvents, startPageStream } from './live.ts';
import {
  CONSENT_HINT, CONSENT_LABEL, KEYWORD_RULES, KIND_LABEL, type MessagingGate, READ_PERMISSIONS, SEND_PERMISSION, STATE_HINT, STATE_LABEL,
  THREAD_KIND_LABEL, gateTitle, messageWindow, messagingGate, moveFocus, segmentLabel, sendBlock,
} from './model.ts';
import { Composer } from './Composer.tsx';
import { NewConversation } from './NewConversation.tsx';
import { CONSENT_ICON, STATE_ICON } from './icons.ts';
import { useBackgroundRefresh } from '../../state/refreshActivity.ts';
import { StaleNote, streamIsDown } from '../../ui/StaleNote.tsx';

const CONSENT_TONE: Record<ConsentState, string> = { opted_in: 'tone-live', pending_confirmation: 'tone-warn', opted_out: 'tone-end', unknown: '' };

/** The live page: owns the controller and the live-update subscription. */
export function MessagingPage({ threadId }: { threadId: string | null }) {
  const ws = useWorkspace();
  const canRead = READ_PERMISSIONS.some(p => hasPermission(ws.permissions, p));
  const [controller] = useState(() => new MessagingController(new MessagingClient(ws.api)));
  const [stream, setStream] = useState<StreamStatus | null>(null);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);

  useEffect(() => {
    if (!canRead) return;
    controller.activate();
    void controller.loadThreads();
    const unsubscribe = messagingEvents.subscribe((event: EventEnvelope) => controller.onEvent(event), () => controller.onResync());
    const stop = startPageStream({ hub: messagingEvents, url: ws.api.url('/events'), onStatus: setStream });
    return () => {
      unsubscribe();
      stop();
      controller.dispose();
    };
    // One controller and one subscription per page mount.
  }, []);

  useEffect(() => {
    if (canRead) controller.openThread(threadId);
  }, [controller, threadId, canRead]);

  return <MessagingView threadId={threadId} snapshot={snapshot} controller={controller} stream={stream} />;
}

/** The page for one decided state (exported for render tests with a mocked controller). */
export function MessagingView({ threadId, snapshot, controller, stream }: { threadId: string | null; snapshot: MessagingSnapshot; controller: MessagingController; stream?: StreamStatus | null }) {
  const ws = useWorkspace();
  const canRead = READ_PERMISSIONS.some(p => hasPermission(ws.permissions, p));
  const gate = messagingGate(ws.config.value?.capabilities['messaging.sms']);
  const [creating, setCreating] = useState(false);
  // Reloading conversations already listed shows the masthead's refresh bar, never a line in the pane.
  useBackgroundRefresh(canRead && snapshot.list.status === 'loading' && snapshot.list.rows.length > 0);
  if (!canRead) {
    return (
      <div className="page">
        <h1 className="page-title">Messages</h1>
        <StatePanel variant="denied" title="You don’t have access to messages" body="Your role doesn’t include reading text conversations. Ask an admin if you need it." details={{ permission: 'messages:read_own' }} />
      </div>
    );
  }
  const thread = threadId ? snapshot.list.rows.find(t => t.id === threadId) ?? null : null;
  return (
    <div className={`split messaging${threadId ? ' has-detail' : ''}`}>
      <section className="list-pane" aria-labelledby="msg-title">
        <div className="pane-head">
          <h1 id="msg-title" className="page-title">Messages</h1>
          <button type="button" className="btn btn-small" onClick={() => setCreating(true)} aria-haspopup="dialog">
            <Icon icon={MessageSquarePlus} /> New conversation
          </button>
        </div>
        <CapabilityNotice gate={gate} />
        <ThreadList snapshot={snapshot} selectedId={threadId} onRetry={() => void controller.loadThreads()} onMore={() => void controller.loadMoreThreads()} />
        <ProgramDetails />
        {/* Always mounted at the foot of the pane, so going stale never moves the conversations. */}
        <StaleNote text={streamIsDown(stream) ? 'Live updates are reconnecting. Conversations may be out of date.' : null} />
      </section>
      <section className="detail-pane" aria-label="Conversation">
        {threadId ? (
          <ThreadPane threadId={threadId} thread={thread} listStatus={snapshot.list.status} view={snapshot.thread} controller={controller} gate={gate} />
        ) : (
          <StatePanel variant="empty" title="Select a conversation" body="Choose a conversation to read it and see each message’s delivery state." />
        )}
      </section>
      <NewConversation open={creating} onClose={() => setCreating(false)} gate={gate} controller={controller} />
    </div>
  );
}

/** The capability's own sentence, always visible while texting isn't available. */
export function CapabilityNotice({ gate }: { gate: MessagingGate }) {
  if (gate.state === 'enabled') return null;
  const tone = gate.state === 'pending_registration' || gate.state === 'unknown' ? 'notice-warn' : 'notice';
  return (
    <div className={`notice ${tone} msg-capability`} role="note" aria-label="Texting status">
      <p className="msg-capability-title"><Icon icon={gate.state === 'pending_registration' ? Hourglass : Info} /> <strong>{gateTitle(gate)}</strong></p>
      <p>{gate.detail}</p>
      <p className="meta">You can still read conversations. Sending is turned on when this changes.</p>
    </div>
  );
}

function ThreadList({ snapshot, selectedId, onRetry, onMore }: { snapshot: MessagingSnapshot; selectedId: string | null; onRetry: () => void; onMore: () => void }) {
  const ws = useWorkspace();
  const { list } = snapshot;
  const listRef = useRef<HTMLUListElement>(null);
  const selectedIndex = Math.max(0, list.rows.findIndex(t => t.id === selectedId));
  const [active, setActive] = useState<number | null>(null);
  const focusIndex = Math.min(active ?? selectedIndex, Math.max(0, list.rows.length - 1));

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const items = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-thread-row]') ?? [])];
    const current = items.findIndex(item => item === e.target);
    if (current < 0) return;
    const next = moveFocus(e.key, current, items.length);
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    items[next]?.focus();
  };

  if (list.status === 'idle' || (list.status === 'loading' && list.rows.length === 0)) return <SkeletonRows label="Loading conversations" />;
  if (list.status === 'error' && list.rows.length === 0 && list.error) return <ErrorPanel error={list.error} onRetry={onRetry} compact />;
  if (list.rows.length === 0) {
    return <StatePanel variant="empty" compact title="No conversations yet" body="Text conversations and your SMS alerts appear here. Nothing is added for show." />;
  }
  return (
    <>
      <p className="meta" id="msg-list-hint">Use the arrow keys to move between conversations, then Enter to open one.</p>
      <ul ref={listRef} className="rows msg-threads" aria-label="Conversations" aria-describedby="msg-list-hint" onKeyDown={onKeyDown}>
        {list.rows.map((t, i) => (
          <ThreadRow key={t.id} thread={t} selected={t.id === selectedId} tabIndex={i === focusIndex ? 0 : -1} onFocus={() => setActive(i)} lineLabel={ws.numbers.value?.find(n => n.id === t.number_id)?.label ?? null} />
        ))}
      </ul>
      {list.skipped > 0 ? <p className="meta">{list.skipped} conversation{list.skipped === 1 ? '' : 's'} couldn’t be shown in this version.</p> : null}
      {list.error ? <p className="field-error" role="alert">{list.error.title}</p> : null}
      {list.nextCursor ? (
        <div className="list-foot">
          <button type="button" className="btn" onClick={onMore} disabled={list.loadingMore} aria-busy={list.loadingMore || undefined}>{list.loadingMore ? 'Loading…' : 'Load more conversations'}</button>
        </div>
      ) : null}
    </>
  );
}

function ThreadRow({ thread, selected, tabIndex, onFocus, lineLabel }: { thread: MessageThread; selected: boolean; tabIndex: number; onFocus: () => void; lineLabel: string | null }) {
  const name = thread.kind === 'system' ? THREAD_KIND_LABEL.system : formatPhone(thread.remote_e164);
  return (
    <li>
      <Link
        to={`/messages/${encodeURIComponent(thread.id)}`}
        className={`row msg-thread${selected ? ' is-selected' : ''}${thread.unread_count > 0 ? ' is-unread' : ''}`}
        aria-current={selected ? 'page' : undefined}
        tabIndex={tabIndex}
        onFocus={onFocus}
        data-thread-row=""
      >
        <span className="row-line1">
          <span className="row-name">{name}</span>
          {thread.last_message_at ? (
            <time className="row-time" dateTime={thread.last_message_at} title={formatAbsolute(thread.last_message_at)} aria-label={formatAbsolute(thread.last_message_at)}>{formatRelative(thread.last_message_at)}</time>
          ) : null}
        </span>
        <span className="row-line2">
          {thread.kind === 'system' ? <span className="row-badge"><Icon icon={BellRing} /> System</span> : <ConsentBadge state={thread.consent_state} />}
          {lineLabel ? <span className="meta">{lineLabel}</span> : null}
          {thread.unread_count > 0 ? (
            <span className="msg-unread"><span aria-hidden="true">{thread.unread_count}</span><span className="visually-hidden">{thread.unread_count} unread</span></span>
          ) : null}
        </span>
        {thread.last_message_preview ? <span className="msg-preview">{thread.last_message_preview}</span> : null}
      </Link>
    </li>
  );
}

export function ConsentBadge({ state }: { state: ConsentState }) {
  return (
    <span className={`row-badge msg-consent ${CONSENT_TONE[state]}`.trim()} title={CONSENT_HINT[state]}>
      <Icon icon={CONSENT_ICON[state]} /> {CONSENT_LABEL[state]}
    </span>
  );
}

/** Keywords and the exact automatic replies, so nobody has to guess what a customer received. */
function ProgramDetails() {
  return (
    <details className="msg-program">
      <summary>Keywords and automatic replies</summary>
      <p className="meta">Sidetone sends these replies automatically when a customer texts a keyword. They show in the thread as “Automatic reply”.</p>
      <dl>
        {KEYWORD_RULES.map(rule => (
          <div key={rule.keywords[0]}>
            <dt className="mono">{rule.keywords.join(', ')}</dt>
            <dd>
              <p>{rule.effect}</p>
              <p className="msg-quote">{rule.reply}</p>
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export function ThreadPane({ threadId, thread, listStatus, view, controller, gate }: {
  threadId: string;
  thread: MessageThread | null;
  listStatus: MessagingSnapshot['list']['status'];
  view: ThreadView | null;
  controller: MessagingController;
  gate: MessagingGate;
}) {
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const canSend = hasPermission(ws.permissions, SEND_PERMISSION);
  const line = thread ? ws.numbers.value?.find(n => n.id === thread.number_id) ?? null : null;
  // Without the thread row (consent, kind) a send could ignore an opt-out: block until it's known.
  const block = thread
    ? sendBlock({ gate, canSend, kind: thread.kind, consent: thread.consent_state })
    : sendBlock({ gate, canSend, consent: null, first: true });
  const blockReason = block.blocked ? (thread || gate.state !== 'enabled' || !canSend ? block.reason : listStatus === 'ready' ? 'This conversation’s details aren’t loaded, so its consent can’t be checked. Reload conversations to send.' : 'Loading this conversation’s details…') : null;
  const title = thread ? (thread.kind === 'system' ? THREAD_KIND_LABEL.system : formatPhone(thread.remote_e164)) : 'Conversation';

  return (
    <div className="detail msg-thread-pane">
      <button type="button" className="btn btn-quiet back-link" onClick={() => navigate('/messages')}><Icon icon={ChevronLeft} /> Back to conversations</button>
      <header className="msg-thread-head">
        <h2 className="detail-title">{title}</h2>
        {thread ? (
          <p className="meta">
            {thread.kind === 'system' ? `Alerts sent to ${formatPhone(thread.remote_e164)}` : thread.remote_e164}
            {line ? <><span className="sep" aria-hidden="true" /> Line: {line.label}</> : null}
          </p>
        ) : listStatus === 'ready' ? (
          <p className="meta">This conversation isn’t in your recent list.</p>
        ) : null}
        {thread && thread.kind === 'customer' ? <ConsentBadge state={thread.consent_state} /> : null}
      </header>
      {thread ? <ConsentBanner thread={thread} /> : null}
      <MessageLog view={view} onRetry={() => controller.retryThread()} onEarlier={() => void controller.loadEarlier()} />
      {thread?.kind === 'system' ? (
        <p className="field-hint msg-readonly">These are automatic alerts to your own phone. Replies can’t be sent from here.</p>
      ) : (
        <Composer key={threadId} draftKey={threadId} blockReason={blockReason} blockCode={block.blocked ? block.code : null} gate={gate}
          onSend={async (body, key) => {
            const result = await controller.send(threadId, body, key);
            // An opt-out refusal is recorded by the server as a suppressed message: show it.
            if (!result.ok && result.error.code === 'consent_opted_out') { void controller.loadThreads({ keepRows: true }); void controller.loadMessages(threadId); }
            return result;
          }}
        />
      )}
    </div>
  );
}

export function ConsentBanner({ thread }: { thread: MessageThread }) {
  if (thread.kind !== 'customer') return null;
  if (thread.consent_state === 'opted_out') {
    return (
      <div className="notice-end msg-banner" role="note">
        <Icon icon={ShieldX} />
        <div><p><strong>This number opted out</strong></p><p>{CONSENT_HINT.opted_out}</p></div>
      </div>
    );
  }
  if (thread.consent_state === 'pending_confirmation') {
    return (
      <div className="notice notice-warn msg-banner" role="note">
        <p><Icon icon={Hourglass} /> <strong>Awaiting YES confirmation</strong></p>
        <p>{CONSENT_HINT.pending_confirmation}</p>
      </div>
    );
  }
  return null;
}

export function MessageLog({ view, onRetry, onEarlier }: { view: ThreadView | null; onRetry: () => void; onEarlier: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const messages = view?.messages ?? [];
  const win = messageWindow(messages.length, expanded);
  const shown = useMemo(() => messages.slice(win.from), [messages, win.from]);
  if (!view || (view.status === 'loading' && messages.length === 0)) return <SkeletonRows rows={3} label="Loading messages" />;
  if (view.status === 'error' && view.error) return <ErrorPanel error={view.error} onRetry={onRetry} compact />;
  return (
    <>
      {view.nextCursor ? (
        <div className="list-foot">
          <button type="button" className="btn btn-small" onClick={onEarlier} disabled={view.loadingEarlier} aria-busy={view.loadingEarlier || undefined}>{view.loadingEarlier ? 'Loading…' : 'Load earlier messages'}</button>
        </div>
      ) : null}
      {win.hidden > 0 ? (
        <div className="list-foot">
          <button type="button" className="btn btn-small" onClick={() => setExpanded(true)}>Show {win.hidden} earlier message{win.hidden === 1 ? '' : 's'}</button>
        </div>
      ) : null}
      {messages.length === 0 ? (
        <p className="field-hint">No messages in this conversation yet.</p>
      ) : (
        <div className="msg-log" role="log" aria-label="Messages" aria-live="polite" aria-relevant="additions">
          <ol className="msg-list">
            {shown.map(m => <MessageItem key={m.id} message={m} />)}
          </ol>
        </div>
      )}
      {view.skipped > 0 ? <p className="meta">{view.skipped} message{view.skipped === 1 ? '' : 's'} couldn’t be shown in this version.</p> : null}
      {view.error ? <p className="field-error" role="alert">{view.error.title}</p> : null}
    </>
  );
}

export function MessageItem({ message: m }: { message: Message }) {
  const ws = useWorkspace();
  const kind = KIND_LABEL[m.kind];
  const outbound = m.direction === 'outbound';
  const who = !outbound ? null : m.kind !== 'conversation' ? 'Sent automatically' : m.actor_user_id ? (ws.userName(m.actor_user_id) ?? 'A teammate') : null;
  return (
    <li className={`msg ${outbound ? 'msg-out' : 'msg-in'}${m.state === 'failed' ? ' is-failed' : ''}${m.state === 'suppressed' ? ' is-suppressed' : ''}`}>
      {kind ? <span className="msg-kind"><Icon icon={m.kind === 'keyword_auto_reply' ? Bot : BellRing} /> {kind}</span> : null}
      <p className="msg-body">{m.body}</p>
      <p className="msg-meta meta">
        <time dateTime={m.created_at} title={formatAbsolute(m.created_at)}>{formatAbsolute(m.created_at)}</time>
        {who ? <><span className="sep" aria-hidden="true" /> {who}</> : null}
        {outbound && m.segments > 0 ? <><span className="sep" aria-hidden="true" /> {segmentLabel(m)}</> : null}
      </p>
      {outbound ? <DeliveryState message={m} /> : null}
    </li>
  );
}

/** Outbound delivery state. The carrier's reason only ever appears inside the details element. */
export function DeliveryState({ message: m }: { message: Message }) {
  const label = STATE_LABEL[m.state];
  return (
    <div className={`msg-state state-${m.state}`}>
      <span className="msg-state-label" title={STATE_HINT[m.state]}><Icon icon={STATE_ICON[m.state]} /> {label}</span>
      {m.state === 'delivered' && m.delivered_at ? <span className="meta"> <time dateTime={m.delivered_at}>{formatAbsolute(m.delivered_at)}</time></span> : null}
      {m.state === 'failed' ? (
        <details className="diagnostics msg-failure">
          <summary>Carrier reason</summary>
          <dl>
            {m.error_detail ? <><dt>Reason</dt><dd>{m.error_detail}</dd></> : null}
            {m.error_code ? <><dt>Code</dt><dd className="mono">{m.error_code}</dd></> : null}
            {!m.error_detail && !m.error_code ? <><dt>Reason</dt><dd>The carrier didn’t give a reason.</dd></> : null}
          </dl>
        </details>
      ) : null}
    </div>
  );
}
